"use client";

// The companion's second entry point into the one planning brain: plan the
// next few hours from where I am right now. Geolocates, then hands the fix
// and horizon to the planner API's next-hours mode. Big touch targets; clear
// fallbacks when location is unavailable or off the network.

import { useState } from "react";
import type { AreaSport } from "@/lib/planner/graph-types";

type Props = {
  sport: AreaSport;
  /** Fire a next-hours plan from a live fix. */
  onPlanNow: (horizonHours: 2 | 3 | 4, coords: { lat: number; lng: number }) => void;
  /** Server said the fix is off the network, or geolocation failed: let the
   * user fall back to the normal day planner with a chosen base. */
  onFallbackToDay: () => void;
  /** A message from the last attempt (off-network, too late, denied). */
  errorMessage: string | null;
  busy: boolean;
};

const HORIZONS: (2 | 3 | 4)[] = [2, 3, 4];

export function RightNowCard({ sport, onPlanNow, onFallbackToDay, errorMessage, busy }: Props) {
  const [horizon, setHorizon] = useState<2 | 3 | 4>(3);
  const [geoState, setGeoState] = useState<"idle" | "locating" | "denied">("idle");
  const isMtb = sport === "mtb";

  const locateAndPlan = () => {
    if (!("geolocation" in navigator)) {
      setGeoState("denied");
      return;
    }
    setGeoState("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoState("idle");
        onPlanNow(horizon, { lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => setGeoState("denied"),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
    );
  };

  return (
    <div className="rounded-[18px] border border-navy/15 bg-navy p-5 text-white md:p-6">
      <h2 className="font-serif text-[22px] font-bold">
        {isMtb ? "On the hill now?" : "On the mountain now?"}
      </h2>
      <p className="mt-1 text-[14px] text-white/70">
        Tell us how long you have. We will look for a nearby mapped starting point and show you where the route begins.
      </p>

      <div className="mt-4 flex gap-2" role="group" aria-label="Time available">
        {HORIZONS.map((h) => (
          <button
            key={h}
            type="button"
            onClick={() => setHorizon(h)}
            aria-pressed={horizon === h}
            className={`min-h-[44px] flex-1 rounded-[12px] border text-[15px] font-semibold transition-colors ${
              horizon === h
                ? "border-gold bg-gold text-white"
                : "border-white/25 bg-white/5 text-white/85 hover:border-gold/60"
            }`}
          >
            {h} hours
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={locateAndPlan}
        disabled={busy || geoState === "locating"}
        className="mt-3 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-[12px] bg-gold px-4 text-[15px] font-semibold text-white transition-colors hover:bg-gold-light disabled:opacity-60"
      >
        {geoState === "locating" ? "Finding you…" : busy ? "Planning…" : "Plan from my location"}
      </button>

      {geoState === "denied" ? (
        <p className="mt-3 text-[13px] leading-relaxed text-white/75">
          We could not get your location. You can still{" "}
          <button
            type="button"
            onClick={onFallbackToDay}
            className="font-semibold text-gold underline-offset-2 hover:underline"
          >
            plan from a lift base
          </button>
          .
        </p>
      ) : null}
      {errorMessage ? (
        <p className="mt-3 rounded-[10px] bg-white/10 px-3 py-2 text-[13px] leading-relaxed text-white/85">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}
